import { getAgentKey } from 'api-hooks/agent';
import { getCustomerKey } from 'api-hooks/customer';
import {
  BalanceAdjusmentTypeEnum,
  BalanceAdjustmentModel,
  BalanceAdjustmentMutationInput,
  BalanceAdjustmentUserTypeEnum,
} from 'api-hooks/transactions/balance-adjustment';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import HOCInput from 'components/common/hoc-input';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { HalfContainer } from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction, { isDataActive } from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import { SectionContainer, Title, TitleContainer } from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  BalanceAdjustmentFormType,
  balanceAdjustmentSchema,
} from './form-type';
import useGetDefaultValues from '../hooks/use-get-default-values';
interface Props {
  balanceAdjustment?: BalanceAdjustmentModel;
  onSubmit: (
    input: BalanceAdjustmentMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<BalanceAdjustmentModel | undefined>;
}

export default function BalanceAdjustmentForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { balanceAdjustment } = props;

  const defaultValues = useGetDefaultValues({ balanceAdjustment });

  const resolver = useYupValidationResolver(balanceAdjustmentSchema());

  const methods = useForm<BalanceAdjustmentFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        values.needApproval =
          values.date.getTime() !== values.tempDate.getTime();
        await props.onSubmit(values, methods as any);
        if (values.userType === BalanceAdjustmentUserTypeEnum.customer) {
          queryClient.refetchQueries(getCustomerKey(values.balanceableId));
        } else {
          queryClient.refetchQueries(getAgentKey(values.balanceableId));
        }
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );
  const isActive = isDataActive(balanceAdjustment?.status);

  const permission = useGetCrudPermission('balance-adjustments');
  const { onVoid, voidMutate, voidPermission } = useVoidAction(
    'balance-adjustments',
    balanceAdjustment,
  );

  const { resolverButton } = useResolveApproval(
    'balance-adjustments',
    balanceAdjustment,
  );
  const { requestApprovalButton } = useApprovalAction(
    'balance-adjustments',
    balanceAdjustment,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'balance-adjustments',
    balanceAdjustment,
  );
  const onRenderRightComponent = React.useMemo(() => {
    if (balanceAdjustment) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [
    balanceAdjustment,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
  ]);

  const balanceableId = methods.getValues('balanceableId');
  React.useEffect(() => {
    if (!balanceableId || balanceableId === '') {
      methods.setValue('balance', 0);
    }
  });

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!balanceAdjustment}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        balanceAdjustment &&
        `${t('common:balance_adjustment')} - ${balanceAdjustment.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={balanceAdjustment}
            onDelete={onVoid}
            noDelete={editable || !voidPermission}
            noEdit={!isActive || !permission.update}
            title={
              balanceAdjustment ? (
                <TitleContainer>
                  <Title variant="HeadingXSmall">{`${t(
                    'common:balance_adjustment',
                  )} - ${balanceAdjustment.number}`}</Title>
                  <TransactionPillStatus value={balanceAdjustment.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', {
                  extra: t('common:balance_adjustment'),
                })
              )
            }
            isDeleteLoading={voidMutate.isLoading}
            deleteText={t('common:void')}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      <div>
        <SectionContainer>
          {!!balanceAdjustment && (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:number')}
                placeholder={t('common:number')}
                required
                disabled
              />
            </HalfContainer>
          )}
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="date"
              clearable={false}
              label={t('common:date')}
              placeholder={t('common:date')}
              hasTime
              required
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="select"
              name="userType"
              options={[
                {
                  id: BalanceAdjustmentUserTypeEnum.agent,
                  label: BalanceAdjustmentUserTypeEnum.agent,
                },
                {
                  id: BalanceAdjustmentUserTypeEnum.customer,
                  label: BalanceAdjustmentUserTypeEnum.customer,
                },
              ]}
              label={t('common:user_type')}
              placeholder={t('common:user_type')}
              onAfterChange={() => {
                methods.setValue('balanceableId', '');
                methods.setValue('balance', 0);
              }}
              required
            />
          </HalfContainer>
          <HOCInput keys={['userType']}>
            {(values) => {
              switch (values.userType) {
                case BalanceAdjustmentUserTypeEnum.agent:
                  return (
                    <HalfContainer>
                      <AgentSelectInput
                        name="balanceableId"
                        onAfterDetailChange={(params) => {
                          methods.setValue(
                            'balance',
                            parseFloat(params.balance.toString()),
                          );
                        }}
                        required
                      />
                    </HalfContainer>
                  );
                case BalanceAdjustmentUserTypeEnum.customer:
                  return (
                    <HalfContainer>
                      <CustomerSelectInput
                        name="balanceableId"
                        onAfterDetailChange={(params) => {
                          methods.setValue(
                            'balance',
                            parseFloat(params.balance.toString()),
                          );
                        }}
                        required
                      />
                    </HalfContainer>
                  );
                default:
                  return <></>;
              }
            }}
          </HOCInput>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="number"
              name="balance"
              label={t('common:current_balance')}
              placeholder={t('common:current_balance')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="select"
              name="type"
              label={t('common:type')}
              options={[
                {
                  id: BalanceAdjusmentTypeEnum.add,
                  label: t(`common:${BalanceAdjusmentTypeEnum.add}`),
                },
                {
                  id: BalanceAdjusmentTypeEnum.substract,
                  label: t(`common:${BalanceAdjusmentTypeEnum.substract}`),
                },
              ]}
              placeholder={t('common:type')}
              required
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="number"
              name="amount"
              label={t('common:amount')}
              placeholder={t('common:amount')}
              required
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text-area"
              name="description"
              label={t('common:description')}
              placeholder={t('common:description')}
            />
          </HalfContainer>
        </SectionContainer>
      </div>
    </Form>
  );
}
